import React, { Component } from "react";
import { Modal, ModalBody } from "reactstrap";
import Icon from "@material-ui/core/Icon";
import { Map, Marker, Polyline } from "google-maps-react";
import ApiEndpoints from "../../../constants/api_endpoints";
import store from "../../../store";
import { setError } from "../../../actions/action-creators";
import moment from "moment";

// Same spherical-mean center + max-distance-from-center zoom heuristic as
// manage_job_field_locations.js's getMapCenterAndZoom() — duplicated
// locally per this codebase's established practice for small single-
// purpose map-math helpers rather than sharing a module for one extra caller.
const degToRad = (deg) => deg * Math.PI / 180;
const radToDeg = (rad) => rad * 180 / Math.PI;
const distanceKm = (lat1, lng1, lat2, lng2) => {
    const radlat1 = Math.PI * lat1 / 180;
    const radlat2 = Math.PI * lat2 / 180;
    const radtheta = Math.PI * (lng1 - lng2) / 180;
    let dist = Math.sin(radlat1) * Math.sin(radlat2) + Math.cos(radlat1) * Math.cos(radlat2) * Math.cos(radtheta);
    dist = Math.acos(Math.min(1, Math.max(-1, dist))) * 180 / Math.PI * 60 * 1.1515 * 1.609344;
    return dist;
}
const getMapCenterAndZoom = (points) => {
    if (!points.length) return { center: { lat: 19.0760, lng: 72.8777 }, zoom: 11 };
    let sumX = 0, sumY = 0, sumZ = 0;
    points.forEach(p => {
        const lat = degToRad(p.lat), lng = degToRad(p.lng);
        sumX += Math.cos(lat) * Math.cos(lng);
        sumY += Math.cos(lat) * Math.sin(lng);
        sumZ += Math.sin(lat);
    });
    const avgX = sumX / points.length, avgY = sumY / points.length, avgZ = sumZ / points.length;
    const lng = Math.atan2(avgY, avgX);
    const lat = Math.atan2(avgZ, Math.sqrt(avgX * avgX + avgY * avgY));
    const center = { lat: radToDeg(lat), lng: radToDeg(lng) };
    const maxDist = Math.max(...points.map(p => distanceKm(center.lat, center.lng, p.lat, p.lng)), 1);
    const zoom = Math.min(15, Math.max(3, parseInt(2000 / maxDist)));
    return { center, zoom };
}

const formatTime = (timestamp) => moment(timestamp, "YYYY-MM-DD HH:mm:ss").format("h:mm A");

class PartnerDayMap extends Component {
    constructor(props) {
        super(props);
        this.state = {
            shifts: [],
            strayLeads: [],
            loaded: false,
        }
    }

    componentDidMount() {
        this.fetchTimeline();
    }

    // Google Maps snapshots its container size at init time. If that
    // happens while the modal is still mid fade-in transition, the map
    // caches a wrong/zero size and its native controls (zoom, satellite
    // toggle) render clipped — fixed by forcing a resize once the modal's
    // open transition has actually finished (reactstrap's onOpened).
    handleMapReady = (mapProps, map) => {
        this.mapInstance = map;
    }

    handleModalOpened = () => {
        if (!this.mapInstance || !window.google) return;
        window.google.maps.event.trigger(this.mapInstance, "resize");
        this.mapInstance.setCenter(this.mapCenter);
    }

    fetchTimeline = async () => {
        try {
            let resp = await ApiEndpoints.FieldSurveyActivity.dayTimeline({
                user_id: this.props.userId,
                job_id: this.props.jobId,
                date: this.props.date
            });
            this.setState({ shifts: resp.data.data.shifts || [], strayLeads: resp.data.data.strayLeads || [], loaded: true });
        }
        catch (e) {
            store.dispatch(setError({ show: true, message: e.response ? e.response.data.message : e.toString() }))
        }
    }

    render() {
        let { onClose, date } = this.props;
        let { shifts, strayLeads, loaded } = this.state;
        let allPoints = shifts.reduce((points, shift) => {
            points.push(shift.punchIn);
            if (shift.punchOut) points.push(shift.punchOut);
            points.push(...shift.leads);
            return points;
        }, []).concat(strayLeads);
        let { center, zoom } = getMapCenterAndZoom(allPoints);
        this.mapCenter = center;

        return (
            <Modal isOpen={true} toggle={onClose} size="lg" centered onOpened={this.handleModalOpened}>
                <div style={{
                    background: "#f1061c", borderRadius: "6px 6px 0 0",
                    padding: "16px 24px", display: "flex", alignItems: "center", justifyContent: "space-between",
                }}>
                    <span style={{ color: "#fff", fontWeight: 700, fontSize: 15 }}>
                        Movement on {moment(date).format("Do MMM'YY")}
                    </span>
                    <button type="button" onClick={onClose}
                        style={{ background: "none", border: "none", color: "#fff", cursor: "pointer", padding: 4 }}>
                        <Icon style={{ fontSize: 20 }}>close</Icon>
                    </button>
                </div>
                <ModalBody style={{ padding: "20px 24px 24px" }}>
                    {!loaded &&
                        <div className="text-center text-muted py-4">Loading...</div>
                    }
                    {loaded && !allPoints.length &&
                        <div className="text-center text-muted py-4"><em>No location data for this day</em></div>
                    }
                    {loaded && !!allPoints.length &&
                        <>
                            <div className="mb-2" style={{ fontSize: 11, color: "#666" }}>
                                <span style={{ color: "#2e7d32", fontWeight: 600 }}>● Punch In</span>
                                <span className="mx-2">·</span>
                                <span style={{ color: "#f1061c", fontWeight: 600 }}>● Punch Out</span>
                                <span className="mx-2">·</span>
                                <span style={{ fontWeight: 600 }}>● Lead added</span>
                                <span className="mx-2">·</span>
                                Hover a pin to see the time
                            </div>
                            <div style={{ height: 380, position: "relative", overflow: "hidden" }}>
                                <Map initialCenter={center} google={window.google} style={{ width: "100%", height: 380 }} zoom={zoom} onReady={this.handleMapReady}>
                                    {shifts.map((shift, i) => {
                                        let path = [shift.punchIn, ...shift.leads, shift.punchOut].filter(Boolean);
                                        return path.length > 1 &&
                                            <Polyline
                                                key={"shift-line-" + i}
                                                path={path}
                                                strokeColor="#666"
                                                strokeOpacity={0.8}
                                                strokeWeight={2}
                                            />
                                    })}
                                    {shifts.map((shift, i) => (
                                        <Marker
                                            key={"punch-in-" + i}
                                            position={{ lat: shift.punchIn.lat, lng: shift.punchIn.lng }}
                                            title={"Punch In — " + formatTime(shift.punchIn.timestamp)}
                                            icon={{ url: "/pin_green.svg", scaledSize: new window.google.maps.Size(28, 28) }}
                                        />
                                    ))}
                                    {shifts.filter(shift => shift.punchOut).map((shift, i) => (
                                        <Marker
                                            key={"punch-out-" + i}
                                            position={{ lat: shift.punchOut.lat, lng: shift.punchOut.lng }}
                                            title={"Punch Out — " + formatTime(shift.punchOut.timestamp)}
                                            icon={{ url: "/pin_red.svg", scaledSize: new window.google.maps.Size(28, 28) }}
                                        />
                                    ))}
                                    {shifts.flatMap(shift => shift.leads).concat(strayLeads).map((lead) => (
                                        <Marker
                                            key={"lead-" + lead.form_response_id}
                                            position={{ lat: lead.lat, lng: lead.lng }}
                                            title={"Lead added — " + formatTime(lead.timestamp)}
                                        />
                                    ))}
                                </Map>
                            </div>
                        </>
                    }
                </ModalBody>
            </Modal>
        );
    }
}

export default PartnerDayMap;
